import { PatientForm } from "./patient-form";
import { Grid, Paper } from "@mui/material";
import EstimatesSidebar from "../shared/estimates-sidebar";
import AvailityLoader from "../../../shared/components/availity-loader";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../shared/state/store";
import { RootState } from "../../../shared/state/root-reducer";
import { getInstanceOfCare } from "@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk";
import { patientService } from "@finpay-development/shared-platform/lib/esm/patient/services/patient-service";
import { PatientEncounter } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter";

interface FormValues {
    clientId?: number;
    clientEmployerId?: number | null;
    payorId?: number | null;
    groupNumber?: string;
    memberId?: string;
}

function PatientTab() {
    const [formValues, setFormValues] = useState<FormValues>({});
    const [isRunningAvailityVOB, setIsRunningAvailityVOB] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const isLoadingIOCRef = useRef(false);
    const lastLoadedPatientIdRef = useRef<number | undefined>(undefined);

    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );
    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );

    useEffect(() => {
        const loadLatestNotManagedIOC = async () => {
            if (!selectedEncounter || !selectedEncounter.patientEncounterId) {
                if (isLoadingIOCRef.current) {
                    return;
                }

                const patientId = selectedPatient?.patientId;
                
                if (!patientId) {
                    return;
                }

                if (lastLoadedPatientIdRef.current === patientId) {
                    return;
                }

                isLoadingIOCRef.current = true;
                lastLoadedPatientIdRef.current = patientId;

                try {
                    const response = await patientService.getPatientInstancesOfCare({
                        patientId: patientId,
                        limit: 100,
                        offset: 0,
                    });

                    if (response.hasErrors) {
                        console.error('Error fetching patient encounters:', response.errorMessage);
                        isLoadingIOCRef.current = false;
                        lastLoadedPatientIdRef.current = undefined;
                        return;
                    }

                    const encounters = response.entity as PatientEncounter[];

                    const encountersWorkflow2 = encounters.filter(
                        (encounter) => encounter.workflow?.workflowId === 2
                    );

                    if (encountersWorkflow2.length > 0) {
                        const latestWorkflow2Ioc = [...encountersWorkflow2].sort((a, b) => {
                            const dateA = new Date(a.instanceCreateDt || 0).getTime();
                            const dateB = new Date(b.instanceCreateDt || 0).getTime();
                            if (dateB !== dateA) {
                                return dateB - dateA;
                            }
                            return (b.patientEncounterId || 0) - (a.patientEncounterId || 0);
                        })[0];

                        await dispatch(getInstanceOfCare({
                            patientId: latestWorkflow2Ioc.patientId,
                            encounterId: latestWorkflow2Ioc.patientEncounterId,
                        }));
                    }
                } catch (error) {
                    console.error('Error loading latest workflow 2 IOC:', error);
                    lastLoadedPatientIdRef.current = undefined;
                } finally {
                    isLoadingIOCRef.current = false;
                }
            }
        };

        loadLatestNotManagedIOC();
    }, [selectedPatient?.patientId, selectedEncounter?.patientEncounterId, dispatch]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 24,
                height: 'auto',
                overflow: 'visible',
                backgroundColor: '#F5F7F9',
            }}
        >
            <div style={{display: 'flex', gap: 24}}>
                <Paper className="px-4 py-4" style={{flex: 2, padding: 16}}>
                    <PatientForm
                        onFormValuesChange={setFormValues}
                        onAvailityRunningChange={setIsRunningAvailityVOB}
                    />
                </Paper>

                <Grid item xs={4}>
                    <AvailityLoader
                        showLoader={isRunningAvailityVOB}
                        customMessage="Running Availity VOB"
                        test-id="patient-availity-loader"
                    />
                    <Paper>
                        <div className="px-4 py-4">
                            <EstimatesSidebar
                                clientId={formValues.clientId}
                                clientEmployerId={formValues.clientEmployerId}
                                payorId={formValues.payorId}
                                groupNumber={formValues.groupNumber}
                                memberId={formValues.memberId}
                            />
                        </div>
                    </Paper>
                </Grid>
            </div>
        </div>
    );
}

export default PatientTab; 